import { useSyncExternalStore } from 'react';
import { t } from '../lib/i18n';

const TYPING_TTL_MS = 4000;

interface Typist {
  userId: string;
  displayName: string;
  until: number;
}

type Listener = () => void;

/** Tiny external store for "is typing" indicators (updates often; kept out of React Query). */
function createTypingStore() {
  let state: Record<string, Typist[]> = {};
  const listeners = new Set<Listener>();
  const emit = () => listeners.forEach((listener) => listener());

  const prune = () => {
    const now = Date.now();
    let changed = false;
    const next: Record<string, Typist[]> = {};
    for (const [channelId, list] of Object.entries(state)) {
      const alive = list.filter((t) => t.until > now);
      if (alive.length !== list.length) changed = true;
      if (alive.length) next[channelId] = alive;
    }
    if (changed) {
      state = next;
      emit();
    }
  };
  setInterval(prune, 1000);

  return {
    add({
      channelId,
      userId,
      displayName,
    }: {
      channelId: string;
      userId: string;
      displayName: string;
    }) {
      const others = (state[channelId] ?? []).filter((t) => t.userId !== userId);
      state = {
        ...state,
        [channelId]: [...others, { userId, displayName, until: Date.now() + TYPING_TTL_MS }],
      };
      emit();
    },
    clear(channelId: string, userId: string) {
      const list = state[channelId];
      if (!list?.some((t) => t.userId === userId)) return;
      state = { ...state, [channelId]: list.filter((t) => t.userId !== userId) };
      emit();
    },
    subscribe: (listener: Listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    get: (channelId: string) => state[channelId],
  };
}

export const typingStore = createTypingStore();

const empty: Typist[] = [];

export function useTyping(channelId: string): Typist[] {
  return useSyncExternalStore(typingStore.subscribe, () => typingStore.get(channelId) ?? empty);
}

/** "Ana está escribiendo…", "Ana y Bruno están escribiendo…", "Varias personas…". */
export function typingLabel(names: string[]): string | null {
  if (names.length === 0) return null;
  if (names.length === 1) return t('{{name}} está escribiendo…', { name: names[0] });
  if (names.length === 2)
    return t('{{first}} y {{second}} están escribiendo…', { first: names[0], second: names[1] });
  return t('Varias personas están escribiendo…');
}
